import { FC } from 'react';
import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../components/AuthContext';
import { useUserStoreActions } from '../hooks/useUser';
import { getErrorMessage } from '../utils/error';
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import { Spinner } from '@/components/ui';
import { config } from '@/app/config';
import { LoginResponse } from '@/types/auth';

const Login: FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const [isLinkedInLoading, setIsLinkedInLoading] = useState<boolean>(false);
  const { setIsLogged } = useAuth();
  const { fetchUser } = useUserStoreActions();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get('redirect');
    if (redirectUrl) {
      sessionStorage.setItem('login_redirect_url', redirectUrl);
    }
  }, []);

  const suffix = import.meta.env.VITE_SAAS_VERS || '';
  const linkedInClientId = config.linkedInClientId || '';
  const linkedInRedirectUri = config.linkedInRedirectUri || window.location.origin + '/login';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await api.post<LoginResponse>('/auth/login', {
        email,
        password,
      });
      setIsLogged(true);

      await fetchUser();

      const userData = response.data.user || response.data;

      const storedRedirect = sessionStorage.getItem('login_redirect_url');
      if (storedRedirect) {
        sessionStorage.removeItem('login_redirect_url');
        navigate(storedRedirect);
      } else if (userData.role === 'external' && userData.wizardCompleted === false) {
        navigate('/wizard');
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err) || 'Failed to login. Please check your credentials.');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    setError('');
    setIsGoogleLoading(true);
    try {
      const token = credentialResponse.credential;

      if (!token) {
        setError('Failed to get credential from Google');
        return;
      }

      const response = await api.post("/auth/google-login", {
        token
      });
      setIsLogged(true);

      await fetchUser();

      // Check if external user needs to complete wizard
      const userData = response.data.user;

      const storedRedirect = sessionStorage.getItem('login_redirect_url');
      if (storedRedirect) {
        sessionStorage.removeItem('login_redirect_url');
        navigate(storedRedirect);
      } else if (userData?.role === 'external' && userData?.wizardCompleted === false) {
        navigate('/wizard');
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      console.error('Google login error:', err);
      setError(getErrorMessage(err) || 'Failed to process Google login');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleLinkedInLogin = () => {
    if (!linkedInClientId) {
      setError('LinkedIn login is not configured');
      return;
    }

    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem('linkedin_oauth_state', state);
    const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${linkedInClientId}&redirect_uri=${encodeURIComponent(linkedInRedirectUri)}&state=${state}&scope=openid%20profile%20email`;
    window.location.href = linkedInAuthUrl;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const storedState = sessionStorage.getItem('linkedin_oauth_state');

    if (code && state && state === storedState) {
      setIsLinkedInLoading(true);
      sessionStorage.removeItem('linkedin_oauth_state');
      window.history.replaceState({}, document.title, '/login');

      api.post('/auth/linkedin-login', { code })
        .then(async (response) => {
          setIsLogged(true);
          await fetchUser();
          const userData = response.data.user;

          const storedRedirect = sessionStorage.getItem('login_redirect_url');
          if (storedRedirect) {
            sessionStorage.removeItem('login_redirect_url');
            navigate(storedRedirect);
          } else if (userData?.role === 'external' && userData?.wizardCompleted === false) {
            navigate('/wizard');
          } else {
            navigate('/dashboard');
          }
        })
        .catch((err) => {
          console.error('LinkedIn login error:', err);
          setError('Failed to process LinkedIn login');
        })
        .finally(() => {
          setIsLinkedInLoading(false);
        });
    }
  }, [navigate, setIsLogged, fetchUser]);

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/carion.jpg"
              alt="Carion"
              className="h-16 object-contain"
            />
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-2">Welcome Back</h2>
          <p className="text-center text-gray-600 mb-8">Sign in to your account</p>

          {/* SSO Buttons */}
          <div className="space-y-3 mb-6">
            {isGoogleLoading ? (
              <div className="flex items-center justify-center py-3 bg-gray-50 rounded-lg">
                <Spinner size="sm" color="gray" />
                <span className="ml-2 text-sm text-gray-600">Authenticating with Google...</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  // Trigger the hidden Google button
                  const googleBtn = document.querySelector('[role="button"][aria-labelledby]') as HTMLElement;
                  if (googleBtn) googleBtn.click();
                }}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg shadow-sm bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>
            )}

            {/* Hidden Google Login component */}
            <div className="hidden">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => {
                  setError("Google login failed. Try again.");
                }}
              />
            </div>

            {isLinkedInLoading ? (
              <div className="flex items-center justify-center py-3 bg-gray-50 rounded-lg">
                <Spinner size="sm" color="gray" />
                <span className="ml-2 text-sm text-gray-600">Authenticating with LinkedIn...</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleLinkedInLogin}
                disabled={!linkedInClientId}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-gray-300 rounded-lg shadow-sm bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0077B5">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                Continue with LinkedIn
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500 font-medium">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2" htmlFor="email">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-semibold text-gray-700" htmlFor="password">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
              <input
                type="password"
                id="password"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 text-center">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-semibold rounded-lg hover:from-gray-800 hover:to-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" color="white" />
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

        </div>
        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-gray-600">Don&apos;t have an account?</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate('/register/external')} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50 cursor-pointer">
              Sign up
            </button>
          </div>
        </div>


        {/* Version */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Version 0.3 {suffix && `• ${suffix}`}
        </p>

      </div>
    </div>
  );
};

export default Login;
